import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { CategoriesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { Badge as CategoryBadge } from './badge'
import { CategoryFilters, CategoryOrder, SortOrder } from '@/types'
import { Button } from '@/components/base/button'
import { ShowDialog } from './show_dialog'
import { NewDialog } from './new_dialog'
import { useTable } from '@/helpers/useTable'
import { dedupe } from '@/helpers/dedupe'

interface Props extends TableProps {
  filters?: CategoryFilters
}

export const Table = ({ title, pageSize = 20, onIndex = false, fixedHeight = false, filters = {} }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<CategoriesQuery>(
    Query,
    { variables: { filters , first: pageSize } }
  )
  const { pageInfo, openNew, setOpenNew, openShow, showShow, hideShow, resourceId } = useTable({ pageInfo: data?.categories?.pageInfo, onIndex, basePath: '/settings/categories' })

  if (error) return <Error message={error.message} />
  if (loading || !data) return <Loading />

  return (
    <>
      <BaseTable<CategoryFilters, CategoryOrder>
        actionButton={<Button color="green" onClick={() => setOpenNew(true)}>Add</Button>}
        defaultSort={{ name: SortOrder.Asc }}
        fixedHeight={fixedHeight}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        title={title}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.categories.nodes = dedupe([
              ...previousData.categories.nodes,
              ...fetchMoreResult.categories.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, nameCont: value })),
            type: 'text',
            placeholder: 'Now, what did we call it?'
          },
        ]}
        headings={[
          { name: 'Name', sortField: 'name', defaultDirection: SortOrder.Asc },
          { name: 'Parent' },
        ]}
        rows={data.categories.nodes.map(category => (
          {
            id: category.id,
            columns: [
              category.name,
              category.parent &&
                <CategoryBadge category={{ color: category.parent.color, name: category.parent.fullname }} />,
            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <ShowDialog id={resourceId} open={openShow} setOpen={hideShow} /> }
      <NewDialog open={openNew} setOpen={setOpenNew} />
    </>
  )
}
